import { Calculation } from '@carbon/react/icons';
import { BundleOfSticksTable } from './bundle-of-sticks-table';
import style from './interest-conversion-section.module.scss';
import { InterestConversionData } from './types';

interface InterestConversionSectionProps {
  data: InterestConversionData | null;
  isLoading: boolean;
  instrumentType?: string | null;
  /** When false, shows a message that calculations are not available for this instrument type */
  enabled?: boolean;
}

const EMPTY_TABLE_DATA: InterestConversionData = {
  executive: {
    interest: { value: null },
    nma: { value: null },
    nmaPostBurden: { value: null },
    nra: { value: null },
  },
  royalty: {
    interest: { value: null },
    nma: { value: null },
    nmaPostBurden: { value: null },
    nra: { value: null },
  },
  bonus: {
    interest: { value: null },
    nma: { value: null },
    nmaPostBurden: { value: null },
    nra: { value: null },
  },
  delay: {
    interest: { value: null },
    nma: { value: null },
    nmaPostBurden: { value: null },
    nra: { value: null },
  },
  leaseRoyaltyRate: {
    interest: { value: null },
    nma: { value: null },
    nmaPostBurden: { value: null },
    nra: { value: null },
  },
  orri: {
    interest: { value: null },
    nma: { value: null },
    nmaPostBurden: { value: null },
    nra: { value: null },
  },
  wi: {
    interest: { value: null },
    nma: { value: null },
    nmaPostBurden: { value: null },
    nra: { value: null },
  },
  nri: {
    interest: { value: null },
    nma: { value: null },
    nmaPostBurden: { value: null },
    nra: { value: null },
  },
};

export const InterestConversionSection = ({
  data,
  isLoading,
  instrumentType = null,
  enabled = true,
}: InterestConversionSectionProps) => {
  // Show "not available" message when instrument type doesn't support BOS calculations
  if (!enabled) {
    return (
      <div className={style.interestConversionSection}>
        <h3 className={style.interestConversionTitle}>Interest Conversion</h3>
        <div className={style.interestConversionEmptyState}>
          <Calculation className={style.calculationIcon} aria-hidden="true" />
          <h4 className={style.emptyStateTitle}>
            Interest calculations not available
          </h4>
          <p className={style.emptyStateDescription}>
            Bundle of Sticks calculations are not available for this instrument
            type.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={style.interestConversionSection}>
      <h3 className={style.interestConversionTitle}>Interest Conversion</h3>
      <span className={style.interestConversionDescription}>
        To calculate interests, provide Gross Acres, Royalty Rate, Net Acreage
        Units, and Tract Net Acres.
      </span>
      {data ? (
        <BundleOfSticksTable
          data={data}
          loading={isLoading}
          instrumentType={instrumentType}
        />
      ) : isLoading ? (
        <BundleOfSticksTable
          data={EMPTY_TABLE_DATA}
          loading={true}
          instrumentType={instrumentType}
        />
      ) : (
        <div className={style.interestConversionEmptyState}>
          <Calculation className={style.calculationIcon} aria-hidden="true" />
          <h4 className={style.emptyStateTitle}>
            Provide inputs to calculate interests
          </h4>
          <p className={style.emptyStateDescription}>
            Enter Gross Acres, Royalty Rate, Net Acreage Units, and Tract Net
            Acres to calculate interests.
          </p>
        </div>
      )}
    </div>
  );
};
